import type { ReactNode } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import type {
  DepositDetail,
  DepositDetailContext,
  DepositRow,
  DepositSourceSettlement,
  DepositReconciliationEntry,
  DepositFeeBreakdown,
  DepositPaymentLinkStatus,
} from '@/lib/services/deposits/deposits.types'
import type { z } from 'zod/v4'
import { DetailCard } from '@/components/patterns/detail-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { cn } from '@/lib/utils'
import { depositQueries, useDepositDetail } from '@/lib/services/deposits/deposits.query'
import { depositDetailSearchSchema } from '@/lib/services/deposits/deposits.schema'
import { getPosCartResumeLink } from '@/lib/pos/cart-links'
import { createFileRoute, Link, notFound, type SearchSchemaInput } from '@tanstack/react-router'
import { ArrowLeft, CalendarClock, Landmark } from 'lucide-react'
import { DepositBreakdown } from './-deposit-breakdown'
import { DepositNotices } from './-deposit-notices'
import {
  formatDepositDate,
  formatDepositDateTime,
  formatDepositSpeed,
  formatMinorCurrency,
  getDepositDescription,
  getEntryActivityLabel,
  getMovementName,
  getSourceSettlementLinkLabel,
  isBankWithdrawal,
  getManualPayoutName,
  isManualDeposit,
  negateMinor,
} from './-presentation'
import { DepositStatus, ReconciliationStatus } from './-status'

export const Route = createFileRoute('/_authenticated/$orgId/reports/deposits/$depositId')({
  component: DepositDetailPage,
  params: {
    parse: ({ depositId, ...params }) => ({ ...params, depositId: Number(depositId) }),
  },
  validateSearch: (search: z.input<typeof depositDetailSearchSchema> & SearchSchemaInput) =>
    depositDetailSearchSchema.parse(search),
  loaderDeps: ({ search }): { search: z.output<typeof depositDetailSearchSchema> } => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('view', 'payouts')) throw notFound()

    await context.queryClient.ensureQueryData(
      depositQueries.detail({
        organizationId: params.orgId,
        depositId: params.depositId,
        context: search,
      })
    )
  },
})

export function DepositDetailPage() {
  const path = usePathHelper()
  const { orgId, depositId } = Route.useParams()
  const search = Route.useSearch()
  const { data: detail } = useDepositDetail(depositId, search)
  const deposit = detail?.deposit
  const context: Partial<DepositDetail['context']> = detail?.context ?? {}

  if (!deposit) return null

  const movementName = getMovementName(deposit)
  const withdrawal = isBankWithdrawal(deposit)
  const manual = isManualDeposit(deposit)
  // A manual or Instant Payout has no transactions of its own to break down.
  const showBreakdown = Boolean(detail?.equation) && !deposit.paid_out_early

  return (
    <div className="space-y-4 pb-8">
      <DepositReturnLink salesOrderId={context.sales_order_id} path={path} orgId={orgId} />

      <AdminPageHeader
        icon={Landmark}
        eyebrow={movementName}
        title={formatMinorCurrency(
          withdrawal ? Math.abs(deposit.amount_minor) : deposit.amount_minor,
          deposit.currency
        )}
        description={getDepositDescription(deposit)}
        badges={
          <div className="flex flex-wrap items-center gap-2">
            <DepositStatus status={deposit.status} movementKind={deposit.movement_kind} />
            {deposit.status === 'paid' && !manual ? (
              <ReconciliationStatus
                status={deposit.entries_sync_status}
                differenceMinor={deposit.reconciliation_difference_minor}
              />
            ) : null}
          </div>
        }
      />

      <DepositNotices deposit={deposit} />

      <div className="@container/deposit-detail">
        <div
          className={cn(
            'grid items-start gap-4',
            showBreakdown && '@xl/deposit-detail:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.75fr)]'
          )}
        >
          {showBreakdown ? (
            <DepositBreakdown
              currency={deposit.currency}
              equation={detail?.equation}
              movementKind={deposit.movement_kind}
            />
          ) : null}
          <DepositIdentity deposit={deposit} />
        </div>
      </div>

      {(detail?.entries?.length ?? 0) > 0 || !['failed', 'canceled'].includes(deposit.status) ? (
        <LedgerCard
          entries={detail?.entries ?? []}
          nextDeposit={
            detail?.equation && 'next_deposit' in detail.equation
              ? detail.equation.next_deposit
              : undefined
          }
          highlightedEntryId={context.entry_id}
          highlightedOrderId={context.sales_order_id}
          path={path}
          orgId={orgId}
          deposit={deposit}
        />
      ) : null}
    </div>
  )
}

function DepositReturnLink({
  salesOrderId,
  path,
  orgId,
}: {
  salesOrderId?: DepositDetailContext['sales_order_id']
  path: ReturnType<typeof usePathHelper>
  orgId: number | string
}) {
  return (
    <Can do="view" on="sales-orders" passThrough>
      {(canViewSalesOrders) => {
        const returnToSalesOrder = !!(salesOrderId && canViewSalesOrders)
        const link = returnToSalesOrder
          ? path.salesOrders.show(salesOrderId).using({ orgId }).link
          : path.deposits.index().using({ orgId }).link

        return (
          <Link
            {...link}
            className="inline-flex items-center gap-2 text-sm font-medium text-interaction underline-offset-2 hover:underline"
          >
            <ArrowLeft className="size-4" />
            {returnToSalesOrder ? `Return to Order #${salesOrderId}` : 'Return to deposits'}
          </Link>
        )
      }}
    </Can>
  )
}

function DepositIdentity({ deposit }: { deposit: DepositRow }) {
  const withdrawal = isBankWithdrawal(deposit)
  const movementName = getMovementName(deposit)
  const fields: [string, ReactNode][] = [
    [getDepositDateLabel(deposit), formatDepositDate(deposit.effective_deposit_at)],
    [withdrawal ? 'Bank account' : 'Destination', deposit.destination?.summary ?? 'Unavailable'],
    ['Transfer speed', formatDepositSpeed(deposit.method)],
    ...(deposit.trace_value
      ? [['Bank trace number', deposit.trace_value] satisfies [string, ReactNode]]
      : []),
  ]

  return (
    <DetailCard accent="periwinkle" contentClassName="space-y-4 p-5">
      <div>
        <Typography variant="eyebrow">{movementName} details</Typography>
        <Typography variant="cardTitle" className="mt-1">
          Details and timeline
        </Typography>
      </div>
      <dl className="space-y-3">
        {fields.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-4">
            <Typography as="dt" variant="supporting">
              {label}
            </Typography>
            <Typography as="dd" variant="label" align="right" className="max-w-[15rem] break-words">
              {value}
            </Typography>
          </div>
        ))}
      </dl>
      <Separator />
      <div className="space-y-2">
        <Typography variant="eyebrow">Timeline</Typography>
        <TimelineEvent label="Created" value={deposit.provider_created_at} />
        <TimelineEvent
          label={withdrawal ? 'Processing' : 'In transit'}
          value={deposit.in_transit_at}
        />
        <TimelineEvent
          label={withdrawal ? 'Collected' : 'Paid'}
          value={deposit.status === 'paid' ? deposit.paid_at : null}
        />
        <TimelineEvent label="Failed" value={deposit.failed_at} />
        <TimelineEvent label="Canceled" value={deposit.canceled_at} />
      </div>
    </DetailCard>
  )
}

function TimelineEvent({
  label,
  value,
}: {
  label: string
  value: DepositRow['provider_created_at']
}) {
  if (!value) return null

  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="inline-flex items-center gap-2 text-muted-foreground">
        <CalendarClock className="size-4" />
        {label}
      </span>
      <span className="text-right tabular-nums">{formatDepositDateTime(value)}</span>
    </div>
  )
}

function LedgerCard({
  entries,
  nextDeposit,
  highlightedEntryId,
  highlightedOrderId,
  path,
  orgId,
  deposit,
}: {
  entries: DepositDetail['entries']
  nextDeposit?: DepositSourceSettlement | null
  highlightedEntryId?: DepositDetailContext['entry_id']
  highlightedOrderId?: DepositDetailContext['sales_order_id']
  path: ReturnType<typeof usePathHelper>
  orgId: string | number
  deposit: DepositRow
}) {
  const manual = isManualDeposit(deposit)
  const payoutName = getManualPayoutName(deposit)
  const instant = deposit.method === 'instant'
  const nextDepositDate = nextDeposit ? formatDepositDate(nextDeposit.effective_deposit_at) : null
  const payoutDescription = [
    `The payments in this ${payoutName}`,
    instant && 'and its Instant Payout fee',
    'appear in your next deposit',
    nextDepositDate && `on ${nextDepositDate}`,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Card>
      <CardHeader className="gap-1.5">
        <CardTitle>
          {isBankWithdrawal(deposit) ? 'Withdrawal transactions' : 'Deposit transactions'}
        </CardTitle>
        <CardDescription>
          {manual
            ? `${instant ? 'Instant Payouts' : 'Manual payouts'} don’t include a list of payments.`
            : 'Each amount included here. Orders appear when we can match them to a payment or refund.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <IndexDataTable
          data={entries}
          columns={getLedgerColumns({
            path,
            orgId,
            highlightedOrderId,
            showPayoutFees: entries.some(
              (entry) => Number(entry.fee_breakdown?.payout_minor ?? 0) !== 0
            ),
          })}
          getRowId={(row) => String(row.id)}
          getRowPresentation={(entry) => ({
            selected: Number(entry.id) === Number(highlightedEntryId),
          })}
          pagination={false}
          emptyState={
            <EmptyState
              size="compact"
              title={manual ? 'No payments to show' : 'Details unavailable'}
              description={
                manual
                  ? `${payoutDescription}.`
                  : 'We do not have the payment and refund breakdown for this transfer.'
              }
              action={
                manual && nextDeposit ? (
                  <Link
                    {...path.deposits.show(nextDeposit.id).using({ orgId }).link}
                    className="font-medium text-interaction underline-offset-2 hover:underline"
                  >
                    View {nextDepositDate} deposit
                  </Link>
                ) : null
              }
            />
          }
        />
      </CardContent>
    </Card>
  )
}

function getLedgerColumns({
  path,
  orgId,
  highlightedOrderId,
  showPayoutFees,
}: {
  path: ReturnType<typeof usePathHelper>
  orgId: number | string
  highlightedOrderId?: DepositDetailContext['sales_order_id']
  showPayoutFees: boolean
}): ColumnDef<DepositReconciliationEntry>[] {
  return [
    {
      accessorKey: 'provider_created_at',
      header: 'Date',
      meta: { label: 'Date', kind: 'date' },
      cell: ({ row }) => formatDepositDate(row.original.provider_created_at),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      meta: { label: 'Type', kind: 'badge' },
      cell: ({ row }) => (
        <div className="space-y-1.5">
          <Badge variant={getEntryTypeVariant(row.original)}>
            {getEntryActivityLabel(row.original)}
          </Badge>
          <SourceSettlementLink
            sourceSettlement={row.original.source_settlement}
            path={path}
            orgId={orgId}
          />
        </div>
      ),
    },
    {
      accessorKey: 'before_fees_minor',
      header: 'Amount',
      meta: { label: 'Amount', kind: 'money', align: 'end' },
      cell: ({ row }) => formatMinorCurrency(row.original.before_fees_minor, row.original.currency),
    },
    ...(
      [
        ['onestem_minor', 'OneStem fees'],
        ['processing_minor', 'Merchant fees'],
        ...(showPayoutFees
          ? ([['payout_minor', 'Instant Payout fees']] satisfies [
              keyof DepositFeeBreakdown,
              string,
            ][])
          : []),
      ] satisfies [keyof DepositFeeBreakdown, string][]
    ).map<ColumnDef<DepositReconciliationEntry>>(([key, label]) => ({
      id: key,
      header: label,
      meta: { label, kind: 'money', align: 'end' },
      cell: ({ row }) =>
        formatMinorCurrency(negateMinor(row.original.fee_breakdown?.[key]), row.original.currency),
    })),
    {
      accessorKey: 'net_minor',
      header: 'Net deposit',
      meta: { label: 'Net deposit', kind: 'money', align: 'end' },
      cell: ({ row }) => (
        <Typography variant="labelStrong" className="tabular-nums">
          {formatMinorCurrency(row.original.net_minor, row.original.currency)}
        </Typography>
      ),
    },
    {
      id: 'sales_orders',
      header: 'Orders',
      meta: { label: 'Orders', kind: 'primary' },
      cell: ({ row }) => (
        <SalesOrdersCell
          entry={row.original}
          highlightedOrderId={highlightedOrderId}
          path={path}
          orgId={orgId}
        />
      ),
    },
  ]
}

function SourceSettlementLink({
  sourceSettlement,
  path,
  orgId,
}: {
  sourceSettlement: DepositSourceSettlement | null
  path: ReturnType<typeof usePathHelper>
  orgId: string | number
}) {
  if (!sourceSettlement) return null

  return (
    <Link
      {...path.deposits.show(sourceSettlement.id).using({ orgId }).link}
      className="block text-xs font-medium text-interaction underline-offset-2 hover:underline"
    >
      {getSourceSettlementLinkLabel(sourceSettlement)}
    </Link>
  )
}

function SalesOrdersCell({
  entry,
  highlightedOrderId,
  path,
  orgId,
}: {
  entry: DepositReconciliationEntry
  highlightedOrderId?: DepositDetailContext['sales_order_id']
  path: ReturnType<typeof usePathHelper>
  orgId: string | number
}) {
  const orders = entry.sales_orders ?? []

  if (orders.length) {
    return (
      <Can do="view" on="sales-orders" passThrough>
        {(canViewSalesOrders) => (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {orders.map((order) =>
              canViewSalesOrders ? (
                <Link
                  key={`${entry.id}-${order.id}`}
                  {...path.salesOrders.show(order.id).using({ orgId }).link}
                  className={
                    Number(order.id) === Number(highlightedOrderId)
                      ? 'font-semibold text-interaction underline'
                      : 'font-medium text-interaction underline-offset-2 hover:underline'
                  }
                >
                  #{order.reference || order.id}
                </Link>
              ) : (
                <Typography key={`${entry.id}-${order.id}`} variant="supporting">
                  #{order.reference || order.id}
                </Typography>
              )
            )}
          </div>
        )}
      </Can>
    )
  }

  const drafts = entry.drafts ?? []

  // Money taken on an order that is still a draft: name the draft so the deposit reconciles.
  if (drafts.length) {
    return (
      <Can do="create" on="sales-orders" passThrough>
        {(canOpenDrafts) => (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {drafts.map(({ cart_id: cartId }) =>
              canOpenDrafts ? (
                <Link
                  key={`${entry.id}-draft-${cartId}`}
                  {...getPosCartResumeLink(path, { orgId, cartId })}
                  className="font-medium text-interaction underline-offset-2 hover:underline"
                >
                  Draft #{cartId}
                </Link>
              ) : (
                <Typography key={`${entry.id}-draft-${cartId}`} variant="supporting">
                  Draft #{cartId}
                </Typography>
              )
            )}
          </div>
        )}
      </Can>
    )
  }

  if (entry.payment_link_status === 'not_applicable') {
    return <Typography variant="supporting">Not related to an order</Typography>
  }

  if (entry.payment_link_status === 'linked') {
    return <Typography variant="supporting">No order</Typography>
  }

  return (
    <Badge variant="warning" size="status">
      {formatOrderMatch(entry.payment_link_status)}
    </Badge>
  )
}

function formatWords(value: string | null | undefined) {
  if (!value) return 'Not specified'

  return String(value)
    .replaceAll('_', ' ')
    .replace(/^\w/, (letter) => letter.toUpperCase())
}

function formatOrderMatch(status: DepositPaymentLinkStatus) {
  const labels: Partial<Record<DepositPaymentLinkStatus, string>> = {
    not_found: 'Not matched',
    ambiguous: 'Needs review',
    source_unavailable: 'Unavailable',
  }

  return labels[status] ?? formatWords(status || 'unavailable')
}

function getEntryTypeVariant(entry: Pick<DepositReconciliationEntry, 'net_minor'>) {
  const netMinor = Number(entry.net_minor)

  if (netMinor > 0) return 'successSoft'
  if (netMinor < 0) return 'destructiveSoft'

  return 'outline'
}

function getDepositDateLabel(
  deposit: Pick<DepositRow, 'effective_date_source' | 'status' | 'movement_kind'>
) {
  if (deposit.effective_date_source === 'failed_at_fallback') return 'Failure date'
  if (deposit.effective_date_source === 'canceled_at_fallback') return 'Cancellation date'
  if (deposit.status === 'failed' || deposit.status === 'canceled') return 'Scheduled date'
  if (deposit.effective_date_source !== 'expected_arrival') {
    return isBankWithdrawal(deposit) ? 'Estimated withdrawal date' : 'Estimated deposit date'
  }

  return isBankWithdrawal(deposit) ? 'Withdrawal date' : 'Deposit date'
}
